import React, { useState } from 'react';
import { BehaviourSupportPlanForm } from '../forms/behaviour-support-plan/BehaviourSupportPlanForm';
import { PersonalisedLearningSupportPlanForm } from '../forms/personalised-learning-support-plan/PersonalisedLearningSupportPlanForm';
import { BehaviourResponsePlanForm } from '../forms/behaviour-response-plan/BehaviourResponsePlanForm';

interface SubOption {
  id: string;
  title: string;
  description: string;
  themeClass: string;
  icon: JSX.Element;
}

const subOptions: SubOption[] = [
  { 
    id: 'behaviour-support', 
    title: 'Behaviour Support Plan', 
    description: 'Create a comprehensive plan to support positive student behaviour',
    themeClass: 'clay-pink-theme',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    )
  },
  { 
    id: 'learning-support', 
    title: 'Individual Education Plan',
    description: 'Develop a personalised plan to enhance student learning outcomes',
    themeClass: 'clay-pink-theme-light',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
      </svg>
    )
  },
  { 
    id: 'behaviour-response', 
    title: 'Behaviour Response Plan', 
    description: 'Develop a comprehensive plan to respond to challenging behaviours',
    themeClass: 'clay-pink-theme-dark',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        <path d="M9 12l2 2 4-4"></path>
      </svg>
    )
  },
];

interface IndividualizedPlanFlowProps {
  onBackToDashboard?: () => void;
}

export const IndividualizedPlanFlow: React.FC<IndividualizedPlanFlowProps> = ({ onBackToDashboard }) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleOptionClick = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleBackClick = () => {
    if (selectedOption === null && onBackToDashboard) {
      // If we're at the top level, go back to dashboard
      onBackToDashboard();
    } else {
      // Otherwise, go back to the options list
      setSelectedOption(null);
    }
  };

  const renderContent = () => {
    if (selectedOption === null) {
      return (
        <div className="flow-suboptions">
          {subOptions.map((option) => (
            <div 
              key={option.id}
              className={`flow-suboption-card ${option.themeClass}`}
              onClick={() => handleOptionClick(option.id)}
            >
              <div className="card-icon-container">
                {option.icon}
              </div>
              <h3>{option.title}</h3>
              <p>{option.description}</p>
              <div className="card-footer">
                <button className="get-quote-link" onClick={() => handleOptionClick(option.id)}>
                  Get Started
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    switch (selectedOption) {
      case 'behaviour-support':
        return <BehaviourSupportPlanForm onBack={handleBackClick} />;
      case 'learning-support':
        return <PersonalisedLearningSupportPlanForm onBack={handleBackClick} />;
      case 'behaviour-response':
        return <BehaviourResponsePlanForm onBack={handleBackClick} />;
      default:
        return null;
    }
  };

  return (
    <div className="document-flow-container">
      <div className="flow-header">
        <h2>Create an Individualised Plan</h2>
        <button className="back-button" onClick={handleBackClick}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </button>
      </div>
      {renderContent()}
    </div>
  );
};
